import { PERMISSION_MATRIX, type CapabilityName, type WorkspacePersonaRole } from '@myplayer/rbac';
import type { MembershipDocument, WorkspaceMembershipRole } from '@myplayer/schema';

export const ADMIN_WORKSPACE_PERSONA_ROLES: readonly WorkspacePersonaRole[] = [
  'ORG_ADMIN',
  'WORKSPACE_MANAGER',
  'CAMPAIGN_EDITOR',
  'COMPLIANCE_OFFICER',
  'VIEWER',
] as const;

export const ASSIGNABLE_MEMBER_ROLES: readonly {
  readonly value: WorkspaceMembershipRole;
  readonly label: string;
  readonly scope: 'Organisation-wide' | 'Single workspace';
  readonly summary: string;
  readonly canDo: readonly string[];
  readonly cannotDo: readonly string[];
}[] = [
  {
    value: 'ORG_ADMIN',
    label: 'Organisation Admin',
    scope: 'Organisation-wide',
    summary: 'Full administrative control across your entire organisation and every workspace.',
    canDo: [
      'Create and update workspaces, custom domains, and organisation settings',
      'Invite, change roles for, or deactivate team members and Partner API keys',
      'Manage products, routing rules, emergency recalls, DSARs, and the Audit Log',
    ],
    cannotDo: ['Modify or deactivate their own membership'],
  },
  {
    value: 'WORKSPACE_MANAGER',
    label: 'Workspace Manager',
    scope: 'Single workspace',
    summary: 'Runs day-to-day product, domain, and safety operations within the workspace.',
    canDo: [
      'Add, manage, and verify custom resolver domains',
      'Create products, stage routing links, and manage batch/serial qualifiers',
      'Activate and clear emergency product safety recalls and export analytics',
    ],
    cannotDo: ['Invite or edit team members, manage Partner API keys, or view the Audit Log'],
  },
  {
    value: 'CAMPAIGN_EDITOR',
    label: 'Campaign Editor',
    scope: 'Single workspace',
    summary: 'Manages product identifiers, batch qualifiers, and marketing routing links.',
    canDo: [
      'Create products, stage draft routing links, and configure conditional rules',
      'Create and update batch/lot and serial qualifiers and sync MyPlayer assets',
    ],
    cannotDo: ['Activate or clear product recalls, manage custom domains, or view the Audit Log'],
  },
  {
    value: 'COMPLIANCE_OFFICER',
    label: 'Compliance Officer',
    scope: 'Single workspace',
    summary: 'Dedicated product safety, recall, and regulatory oversight role.',
    canDo: [
      'Activate and clear emergency product safety recalls',
      'Inspect and export the tamper-evident Audit Log and scan analytics',
    ],
    cannotDo: ['Create or edit product links, manage custom domains, or invite team members'],
  },
  {
    value: 'VIEWER',
    label: 'Viewer (Read-only)',
    scope: 'Single workspace',
    summary: 'Read-only visibility across workspace products, routing links, and analytics.',
    canDo: [
      'View products, GTINs, qualifiers, routing rules, domains, and team members',
      'View and export scan analytics',
    ],
    cannotDo: ['Create or edit products, trigger recalls, change settings, or view the Audit Log'],
  },
] as const;

export function resolveDisplayedMemberRole(
  member: MembershipDocument,
  activeWorkspaceId: string,
): WorkspaceMembershipRole {
  if (member.orgRole === 'ORG_ADMIN') return 'ORG_ADMIN';
  return (
    member.workspaceRoles[activeWorkspaceId] ?? Object.values(member.workspaceRoles)[0] ?? 'VIEWER'
  );
}

export function isSelfMember(
  member: MembershipDocument,
  session: { readonly uid: string; readonly email: string | null } | null,
): boolean {
  return Boolean(
    session &&
    (member.uid === session.uid || session.email?.toLowerCase() === member.email.toLowerCase()),
  );
}

export function mapSnapshotToMemberships(
  docs: readonly { readonly id: string; data(): Partial<MembershipDocument> }[],
  orgId: string,
): readonly MembershipDocument[] {
  const byUid = new Map<string, MembershipDocument>();
  for (const docSnap of docs) {
    const d = docSnap.data();
    const uid = d.uid ?? docSnap.id;
    const m: MembershipDocument = {
      membershipId: d.membershipId ?? docSnap.id,
      uid,
      email: d.email ?? '',
      orgId: d.orgId ?? orgId,
      orgRole: d.orgRole ?? null,
      workspaceRoles: d.workspaceRoles ?? {},
      status: d.status ?? 'ACTIVE',
      createdAt: d.createdAt ?? '',
      updatedAt: d.updatedAt ?? '',
      updatedBy: d.updatedBy ?? '',
    };
    const p = byUid.get(uid);
    if (!p) {
      byUid.set(uid, m);
      continue;
    }
    const status =
      p.status === 'ACTIVE' || m.status === 'ACTIVE'
        ? 'ACTIVE'
        : p.status === 'INVITED' || m.status === 'INVITED'
          ? 'INVITED'
          : m.status;
    byUid.set(uid, {
      ...p,
      ...(m.membershipId.includes('_ORG_') ? { membershipId: m.membershipId } : {}),
      orgRole:
        p.orgRole === 'ORG_ADMIN' || m.orgRole === 'ORG_ADMIN'
          ? 'ORG_ADMIN'
          : (p.orgRole ?? m.orgRole),
      workspaceRoles: { ...p.workspaceRoles, ...m.workspaceRoles },
      status,
      updatedAt: m.updatedAt > p.updatedAt ? m.updatedAt : p.updatedAt,
    });
  }
  return Array.from(byUid.values()).sort((a, b) => a.email.localeCompare(b.email));
}

const ROLE_PLURAL_LABELS: Readonly<Record<WorkspacePersonaRole, string>> = {
  SUPER_ADMIN: 'Super Admins',
  ORG_ADMIN: 'Organisation Admins',
  WORKSPACE_MANAGER: 'Workspace Managers',
  CAMPAIGN_EDITOR: 'Campaign Editors',
  COMPLIANCE_OFFICER: 'Compliance Officers',
  VIEWER: 'Viewers',
};

export function getAuthorizedPersonaRoles(
  capability: CapabilityName,
): readonly WorkspacePersonaRole[] {
  const def = PERMISSION_MATRIX[capability];
  return ADMIN_WORKSPACE_PERSONA_ROLES.filter((role) => def.permissions[role]);
}

export function formatRoleList(roles: readonly WorkspacePersonaRole[]): string {
  const labels = roles.map((role) => ROLE_PLURAL_LABELS[role]);
  if (labels.length === 0) return 'authorised administrators';
  if (labels.length === 1) return labels[0] ?? 'authorised administrators';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

export function formatCapabilityRestrictionNotice(params: {
  readonly capability: CapabilityName;
  readonly actionSummary: string;
  readonly callerRole?: WorkspacePersonaRole | undefined;
}): string {
  const allowedRoles = formatRoleList(getAuthorizedPersonaRoles(params.capability));
  const roleTag = params.callerRole ? ` (${params.callerRole})` : '';
  return `Read-only for your role${roleTag}: Only ${allowedRoles} can ${params.actionSummary}.`;
}
